import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag, Select, Input, DatePicker, Button } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import css from "./OrderChangelog.module.css";
import { changelogActions } from "../../../store/slices/messages-slices";

const { RangePicker } = DatePicker;

const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  { value: "DISH_REPLACED", label: "Dish replaced" },
  { value: "PROTEIN_REPLACED", label: "Protein changed" },
  { value: "SAUCE_REPLACED", label: "Sauce changed" },
  { value: "PROTEIN_REMOVED", label: "Protein removed" },
  { value: "SAUCE_REMOVED", label: "Sauce removed" },
  { value: "DATE_TRANSFERRED", label: "Date transferred" },
];

const ACTION_COLORS = {
  DISH_REPLACED: "blue",
  PROTEIN_REPLACED: "purple",
  SAUCE_REPLACED: "cyan",
  PROTEIN_REMOVED: "orange",
  SAUCE_REMOVED: "volcano",
  DATE_TRANSFERRED: "geekblue",
};

function OrderChangelog() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data, total, status } = useSelector((store) => store.changelog);

  const [query, setQuery] = useState({ page: 1, limit: 20 });
  const [orderIdInput, setOrderIdInput] = useState("");
  const [action, setAction] = useState("");
  const [dateRange, setDateRange] = useState(null);

  useEffect(() => {
    dispatch(changelogActions.getChangelog(query));
  }, [dispatch, query]);

  const handleSearch = () => {
    const params = { page: 1, limit: 20 };
    if (action) params.action = action;
    if (orderIdInput.trim()) params.orderId = orderIdInput.trim();
    if (dateRange) {
      params.startDate = dateRange[0].startOf("day").toISOString();
      params.endDate = dateRange[1].endOf("day").toISOString();
    }
    setQuery(params);
  };

  const handleReset = () => {
    setOrderIdInput("");
    setAction("");
    setDateRange(null);
    setQuery({ page: 1, limit: 20 });
  };

  const columns = [
    {
      title: t("col-date"),
      dataIndex: "createdAt",
      width: 160,
      render: (v) => dayjs(v).format("DD.MM.YYYY HH:mm"),
    },
    {
      title: t("col-order"),
      key: "order",
      width: 100,
      render: (_, row) => (
        <span className={css.orderLink}>#{row.orderId}</span>
      ),
    },
    {
      title: t("col-client"),
      key: "client",
      width: 150,
      render: (_, row) => row.order?.client?.clientName || "—",
    },
    {
      title: t("col-action"),
      dataIndex: "action",
      width: 160,
      render: (v) => (
        <Tag color={ACTION_COLORS[v] || "default"} className={css.actionTag}>
          {ACTION_OPTIONS.find((o) => o.value === v)?.label || v}
        </Tag>
      ),
    },
    {
      title: t("col-description"),
      dataIndex: "description",
      render: (v) => v || "—",
    },
    {
      title: t("col-by"),
      key: "user",
      width: 160,
      render: (_, row) =>
        row.user ? (
          <div className={css.userCell}>
            <span className={css.userName}>{row.user.clientName || "—"}</span>
            <span className={css.userRole}>{row.user.role}</span>
          </div>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <div className={css.wrap}>
      <div className={css.filters}>
        <Input
          placeholder="Order ID"
          value={orderIdInput}
          onChange={(e) => setOrderIdInput(e.target.value)}
          style={{ width: 120 }}
          allowClear
        />
        <Select
          value={action}
          onChange={setAction}
          options={ACTION_OPTIONS}
          style={{ width: 180 }}
        />
        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          format="DD.MM.YYYY"
        />
        <Button type="primary" onClick={handleSearch}>
          Search
        </Button>
        <Button onClick={handleReset}>Reset</Button>
      </div>

      <Table
        rowKey="id"
        dataSource={data}
        columns={columns}
        loading={status === "loading"}
        pagination={{
          current: query.page,
          pageSize: query.limit,
          total,
          showSizeChanger: false,
          onChange: (page) => setQuery((q) => ({ ...q, page })),
        }}
        size="small"
        scroll={{ x: 900 }}
      />
    </div>
  );
}

export default OrderChangelog;
